import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { User, Package, AlertCircle } from 'lucide-react';
import ordersApi from 'api/requests/orders';
import currency from 'helpers/currency';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import breakpoints from 'theme/breakpoints';
import OrderMoneyChain from './OrderMoneyChain';

/**
 * Conteúdo revelado ao abrir uma linha da lista.
 *
 * Duas colunas porque respondem perguntas diferentes: à esquerda **quem comprou
 * o quê** (vem de `/orders/detail`, buscado só quando a linha abre), à direita
 * **como o dinheiro se decompôs** (já veio na própria transação, então aparece
 * na hora, sem esperar a rede).
 *
 * Separar as duas fontes é de propósito: se o detalhe do pedido falhar, a
 * cadeia do dinheiro continua na tela — é ela que o produtor vem conferir.
 */
export default function OrderDetailPanel({ transaction }) {
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    const buyer = transaction?.customer;

    useEffect(() => {
        let active = true;

        async function loadOrder() {
            setLoading(true);
            setFailed(false);

            try {
                const { data } = await ordersApi.detail(transaction?.order_id);
                if (active) setOrder(data?.data);
            } catch (error) {
                if (active) setFailed(true);
            } finally {
                if (active) setLoading(false);
            }
        }

        loadOrder();

        return () => { active = false; };
    }, [transaction?.order_id]);

    return (
        <Panel>
            <Side>
                <SideTitle>
                    <User size={14} color={colors.night} />
                    Comprador
                </SideTitle>

                {/* Quando a linha já traz o comprador, ele aparece na hora: esperar
                    a rede para reimprimir um nome que está a 40px de distância só
                    faria o painel piscar. O cupom continua vindo do detalhe. */}
                {buyer ? (
                    <>
                        <CustomerName>{buyer.name || 'Comprador não identificado'}</CustomerName>
                        {buyer.email && <CustomerMeta>{buyer.email}</CustomerMeta>}
                    </>
                ) : (
                    <>
                        {loading && <SkeletonLine />}

                        {!loading && failed && (
                            <Unavailable>
                                <AlertCircle size={14} />
                                Não foi possível carregar os dados do comprador agora.
                            </Unavailable>
                        )}

                        {!loading && !failed && (
                            <>
                                <CustomerName>{order?.customer?.name || 'Comprador não identificado'}</CustomerName>
                                {order?.customer?.email && <CustomerMeta>{order.customer.email}</CustomerMeta>}
                            </>
                        )}
                    </>
                )}

                {order?.coupon && <Coupon>Cupom {order.coupon}</Coupon>}

                <SideTitle $spaced>
                    <Package size={14} color={colors.night} />
                    Itens
                    {order?.products?.length > 0 && <Count>{order.products.length}</Count>}
                </SideTitle>

                {loading && <SkeletonLine />}

                {!loading && !failed && (
                    order?.products?.length > 0 ? (
                        <Items>
                            {order.products.map((item, index) => (
                                <Item key={`${item?.name}-${index}`}>
                                    <ItemName>
                                        <Quantity>{item?.quantity}×</Quantity>
                                        {item?.name}
                                    </ItemName>
                                    <ItemPrice>{currency.format(item?.price)}</ItemPrice>
                                </Item>
                            ))}
                        </Items>
                    ) : (
                        <Unavailable>Este pedido não lista itens.</Unavailable>
                    )
                )}
            </Side>

            <Side>
                <SideTitle>Para onde foi o dinheiro</SideTitle>
                <OrderMoneyChain transaction={transaction} />
            </Side>
        </Panel>
    );
}

const Panel = styled.div`
    display: grid;
    gap: 24px;
    padding: 18px;
    background: #FAFAFB;
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;

    @media screen and (min-width: ${breakpoints.smd}) {
        grid-template-columns: 1fr 1fr;
        gap: 32px;
        padding: 20px 22px;
    }
`;

const Side = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const SideTitle = styled.h4`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: ${({ $spaced }) => $spaced ? '20px 0 8px' : '0 0 8px'};
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${colors.night};
`;

const Count = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
    background: ${colors.neutralDark};
    border-radius: 50px;
    padding: 1px 7px;
    letter-spacing: 0;
`;

const CustomerName = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    word-break: break-word;
`;

const CustomerMeta = styled.span`
    font-size: 13px;
    color: ${colors.night};
    margin-top: 2px;
    word-break: break-all;
`;

const Coupon = styled.span`
    align-self: flex-start;
    margin-top: 8px;
    padding: 2px 10px;
    border-radius: 50px;
    border: 1px solid #DFC8F8;
    background: #F7EFFF;
    color: #6D14CC;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
`;

const Items = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 6px;
    list-style: none;
    padding: 0;
    margin: 0;
`;

const Item = styled.li`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
`;

const ItemName = styled.span`
    display: flex;
    gap: 6px;
    font-size: 13px;
    color: ${colors.nightDark};
    line-height: 1.35;
`;

const Quantity = styled.span`
    font-weight: 600;
    color: ${colors.night};
    flex-shrink: 0;
`;

const ItemPrice = styled.span`
    font-size: 13px;
    color: ${colors.night};
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
`;

const Unavailable = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: ${colors.night};
`;

const SkeletonLine = styled.div`
    height: 14px;
    width: 70%;
    border-radius: 4px;
    background: linear-gradient(90deg, ${colors.neutralDark} 25%, ${colors.neutral} 50%, ${colors.neutralDark} 75%);
    background-size: 200% 100%;
    animation: shimmer 1.2s infinite linear;

    @keyframes shimmer {
        from { background-position: 200% 0; }
        to { background-position: -200% 0; }
    }
`;
